import React, { useState, useEffect } from "react";
import { apiAddGiving, apiUpdateGiving } from "./api";

const CATEGORIES = ["Tithe", "Offering", "Special Donation", "Building Fund", "Other"];

export default function GivingFormModal({ userId, entry, onSaved, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: entry?.date || today,
    amount: entry?.amount ?? "",
    category: entry?.category || "Tithe",
    notes: entry?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Trap ESC to close
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [busy, onClose]);

  const submit = async () => {
    setErr("");
    const amt = Number(form.amount);
    if (!form.date) { setErr("Date is required."); return; }
    if (!form.amount || Number.isNaN(amt) || amt <= 0) { setErr("Amount must be a positive number."); return; }
    setBusy(true);
    try {
      const payload = { date: form.date, amount: amt, category: form.category, notes: form.notes };
      const saved = entry?.id
        ? await apiUpdateGiving(userId, entry.id, payload)
        : await apiAddGiving(userId, payload);
      onSaved && onSaved(saved);
    } catch (e) {
      setErr(e.message || "Could not save");
    }
    setBusy(false);
  };

  return (
    <div
      className="modal-overlay"
      data-testid="giving-form-modal"
      onClick={e => e.target === e.currentTarget && !busy && onClose()}
    >
      <div className="modal" style={{ maxWidth: 440, background: "var(--kc-elevated)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)" }}>
        <div className="modal-header" style={{ borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)" }}>
          <div className="modal-title" style={{ fontFamily: "'Cinzel',serif", color: "var(--kc-accent)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {entry ? "Edit Entry" : "New Giving Entry"}
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group mb-14">
            <label>Date *</label>
            <input
              type="date"
              data-testid="giving-form-date"
              value={form.date}
              onChange={e => set("date", e.target.value)}
              max={today}
            />
          </div>
          <div className="form-group mb-14">
            <label>Amount *</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--kc-accent)", fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700 }}>$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                data-testid="giving-form-amount"
                value={form.amount}
                onChange={e => set("amount", e.target.value)}
                placeholder="0.00"
                style={{ paddingLeft: 26 }}
              />
            </div>
          </div>
          <div className="form-group mb-14">
            <label>Category</label>
            <select
              data-testid="giving-form-category"
              value={form.category}
              onChange={e => set("category", e.target.value)}
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea
              data-testid="giving-form-notes"
              rows={2}
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
              placeholder="e.g. Service of 06 Jul"
              style={{ resize: "vertical" }}
            />
          </div>
          {err && (
            <div data-testid="giving-form-error" style={{
              marginTop: 10, padding: "8px 12px", borderRadius: 8,
              background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.40)",
              color: "#ff6b6b", fontSize: 12, fontFamily: "'Montserrat',sans-serif",
            }}>{err}</div>
          )}
        </div>
        <div className="modal-footer" style={{ borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)" }}>
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-gold" data-testid="giving-form-save" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
